import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { RefreshCw, Loader2 } from 'lucide-react';
import { Pill } from '../../../shared/Pill';
import RowActionsMenu from '../../../shared/RowActionsMenu';
import CancelButton from '../../../shared/CancelButton';
import { useBulkSelection, BulkSelectBar, SelectPageCheckbox, SelectRowCheckbox } from '../../../shared/BulkSelect';
import { QuickBooksItemPicker, QuickBooksConnectionNotice, QuickBooksItemStatusPill, itemLabel } from '../../../shared/QuickBooksItemPicker';
import { useErrorToast, useDismissError } from '../../../shared/ErrorToastContext';
import { formatDateTime } from '../../../shared/tenantClock';
import { formatNumber } from '../../../shared/numberFormat';
import { LocalPagination, useLocalPagination } from '../../../shared/pagination';
import UnderlinedTabs from './UnderlinedTabs';
import FieldHelp from '../../../shared/FieldHelp';
import '../../../shared/ListRowHover.css';
import '../../../shared/StyledInput.css';
import { askConfirm } from '../../../shared/AppDialogs';

// QuickBooks > Products (issue #265, vis_quickbooks_integration.md
// "Products" and "Bulk Matching for Existing Records"): link each Product to
// its existing QuickBooks Item. Unmatched lists active Products with no
// link, each with any suggestion (SKU match first, else a single exact name
// match); Matched and Needs Attention list the links, the latter where the
// Item has been made inactive or is gone in QuickBooks. Unlinked Items lists
// the active Inventory Items no Product has yet. Optim never creates or
// edits an Item.

const TH = { padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' };
const TD = { padding: '8px', fontSize: 14, verticalAlign: 'middle' };
const SMALL_PRIMARY = { padding: '5px 12px', border: 'none', borderRadius: 6, background: '#1976d2', color: '#fff', fontSize: 13, fontWeight: 500, cursor: 'pointer' };
const LINK_BUTTON = { background: 'none', border: 'none', color: '#1976d2', textDecoration: 'underline', cursor: 'pointer', fontSize: 13, padding: 0 };

const REASON_PILLS = {
  sku: { label: 'SKU match', color: '#1a7f4b', background: 'rgba(26, 127, 75, 0.12)' },
  name: { label: 'Name match', color: '#92400e', background: 'rgba(217, 119, 6, 0.14)' },
};

function matchesSearch(row, term) {
  if (!term) return true;
  const t = term.toLowerCase();
  const fields = [row.product.sku, row.product.name, row.link?.qbo_item_name, row.link?.qbo_item_sku, row.suggestion?.item.full_name, row.suggestion?.item.sku];
  return fields.some((f) => f && String(f).toLowerCase().includes(t));
}

function QuickBooksProductsTab({ canEdit }) {
  const navigate = useNavigate();
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [list, setList] = useState('unmatched');
  const [search, setSearch] = useState('');
  // The row whose picker is open, and the Item chosen in it.
  const [editing, setEditing] = useState(null);
  const [picked, setPicked] = useState(null);
  const [busy, setBusy] = useState(false);
  // ?product= (from a Sales Order line's "Not linked to QuickBooks"): open
  // on that Product's row with the picker open and its suggestion picked.
  const [searchParams] = useSearchParams();
  const focusProductId = searchParams.get('product');
  const focusedRef = useRef(false);

  const load = async ({ refresh = false } = {}) => {
    setLoading(true);
    try {
      const res = await axios.get('/api/quickbooks/products', { params: refresh ? { refresh: 1 } : {} });
      setData(res.data.data);
    } catch (err) {
      showError(err, 'Failed to load QuickBooks matches');
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  const connected = data?.connection?.status === 'connected';
  const editable = canEdit && connected;

  useEffect(() => {
    if (!data || !focusProductId || focusedRef.current) return;
    focusedRef.current = true;
    const listKey = ['unmatched', 'needs_attention', 'matched'].find((key) => data[key].some((r) => String(r.product.id) === focusProductId));
    if (!listKey) return;
    const row = data[listKey].find((r) => String(r.product.id) === focusProductId);
    setList(listKey);
    setSearch(row.product.sku);
    if (editable) {
      setEditing(row.product.id);
      setPicked(row.suggestion?.item || null);
    }
  }, [data, focusProductId, editable]);

  const rows = useMemo(() => (list === 'items' ? [] : (data?.[list] || []).filter((row) => matchesSearch(row, search))), [data, list, search]);
  // Unlinked Items, each with the Product it's suggested for, if any.
  const itemRows = useMemo(() => {
    if (list !== 'items' || !data) return [];
    const suggestedFor = new Map(data.unmatched.filter((r) => r.suggestion).map((r) => [r.suggestion.item.id, r.product]));
    const t = search.toLowerCase();
    return data.items
      .filter((item) => !t || [item.full_name, item.sku].some((f) => f && f.toLowerCase().includes(t)))
      .map((item) => ({ item, suggestedFor: suggestedFor.get(item.id) || null }));
  }, [data, list, search]);
  const paging = useLocalPagination(list === 'items' ? itemRows : rows, `${list}|${search}`);
  const acceptableIn = (some) => (list === 'unmatched' && editable ? some.filter((r) => r.suggestion).map((r) => r.product.id) : []);
  const selection = useBulkSelection({ pageIds: acceptableIn(paging.pageRows), total: acceptableIn(rows).length, resetKey: `${list}|${search}|${data?.fetched_at}` });

  const openPicker = (productId) => { setEditing(productId); setPicked(null); };
  const closePicker = () => { setEditing(null); setPicked(null); };

  const link = async (productId, item) => {
    setBusy(true);
    try {
      await axios.put(`/api/quickbooks/products/${productId}`, { qbo_item_id: item.id });
      closePicker();
      dismissError();
      await load();
    } catch (err) {
      showError(err, 'Linking failed');
    }
    setBusy(false);
  };

  const unlink = async (row) => {
    if (!(await askConfirm(`Remove the QuickBooks match for ${row.product.sku}?\n\nIt was linked to “${row.link.qbo_item_name}”. Nothing changes in QuickBooks.`, { confirmLabel: 'Remove', destructive: true }))) return;
    setBusy(true);
    try {
      await axios.delete(`/api/quickbooks/products/${row.product.id}`);
      dismissError();
      await load();
    } catch (err) {
      showError(err, 'Removing the match failed');
    }
    setBusy(false);
  };

  const acceptSelected = async () => {
    const chosen = rows.filter((r) => r.suggestion && selection.isSelected(r.product.id));
    if (!chosen.length) return;
    if (!(await askConfirm(`Link ${chosen.length} Product${chosen.length === 1 ? '' : 's'} to their suggested QuickBooks Items?`, { confirmLabel: 'Link' }))) return;
    setBusy(true);
    try {
      await axios.post('/api/quickbooks/products/bulk-link', {
        links: chosen.map((r) => ({ product_id: r.product.id, qbo_item_id: r.suggestion.item.id })),
      });
      selection.clear();
      dismissError();
      await load();
    } catch (err) {
      showError(err, 'Accepting suggestions failed');
    }
    setBusy(false);
  };

  const openProduct = (productId) => navigate(`/products?edit=${productId}`);

  // Each list's "?" — editable in Field Help Settings.
  const listHelp = (key, label, text) => (
    <FieldHelp fieldKey={`accounting-quickbooks.${key}`} pageKey="accounting-quickbooks" label={`QuickBooks — ${label}`}>{text}</FieldHelp>
  );
  const lists = [
    {
      key: 'unmatched',
      label: `Unmatched (${formatNumber(data?.unmatched.length || 0)})`,
      help: listHelp('unmatched', 'Unmatched', 'Active Optim Products not yet linked to a QuickBooks Item. Where an Item has the same SKU, or is the only Item with the same name, it’s suggested; accept it or choose another. Every Product that will be sold through Optim needs a match.'),
    },
    {
      key: 'matched',
      label: `Matched (${formatNumber(data?.matched.length || 0)})`,
      help: listHelp('matched', 'Matched', 'Optim Products linked to an active QuickBooks Inventory Item. The link holds even if either side is renamed.'),
    },
    {
      key: 'needs_attention',
      label: `Needs Attention (${formatNumber(data?.needs_attention.length || 0)})`,
      help: listHelp('needs-attention', 'Needs Attention', 'Optim Products still linked to a QuickBooks Item that has since been made inactive (QuickBooks calls this deleted) or is no longer an Inventory Item. Reactivate the Item in QuickBooks, or change the match to another Item. Refresh from QuickBooks to re-check.'),
    },
    {
      key: 'items',
      label: `Unlinked Items (${formatNumber(data?.items.length || 0)})`,
      help: listHelp('unlinked-items', 'Unlinked Items', 'The other way round: active QuickBooks Inventory Items no Optim Product is linked to. Many Items are expected to stay QuickBooks-only and never appear in Optim, so this list is for reference. Links are always made from the Optim Product.'),
    },
  ];

  // The inline picker that replaces a row's Item cell while it's open.
  const renderPicker = (productId) => (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
      <div style={{ flex: 1, minWidth: 220 }}>
        <QuickBooksItemPicker items={data.items} value={picked?.id} onChange={setPicked} autoFocus disabled={busy} />
      </div>
      <button type="button" onClick={() => picked && link(productId, picked)} disabled={!picked || busy} title="Links this Product to the chosen QuickBooks Item" style={{ ...SMALL_PRIMARY, opacity: !picked || busy ? 0.6 : 1, cursor: !picked || busy ? 'not-allowed' : 'pointer' }}>
        Link
      </button>
      <CancelButton onClick={closePicker} disabled={busy} />
    </div>
  );

  const productCells = (row) => (
    <>
      <td data-label="SKU" style={{ ...TD, fontWeight: 600 }}>{row.product.sku}</td>
      <td className="cell-title" style={TD}>
        <button type="button" onClick={() => openProduct(row.product.id)} title="Opens this Product" style={{ ...LINK_BUTTON, fontSize: 14, textDecoration: 'none', color: '#222', textAlign: 'left' }}>
          {row.product.name}
        </button>
        {!row.product.is_active && <span style={{ marginLeft: 6, fontSize: 12, color: '#888' }}>(inactive)</span>}
      </td>
    </>
  );

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', margin: '16px 24px 0' }}>
        <input
          className="styled-input"
          placeholder="Search SKU, name or Item..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: 240 }}
        />
        <button
          type="button"
          onClick={() => load({ refresh: true })}
          disabled={loading}
          title="Fetches the Items from QuickBooks again"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 12px', borderRadius: 6, border: '1px solid #bfc8d6', background: '#fff', fontSize: 14, fontWeight: 500, cursor: loading ? 'not-allowed' : 'pointer' }}
        >
          {loading ? <Loader2 size={15} style={{ animation: 'qb-spin 0.8s linear infinite' }} /> : <RefreshCw size={15} />}
          Refresh from QuickBooks
        </button>
        {data?.fetched_at && (
          <span style={{ fontSize: 13, color: '#666' }}>
            {formatNumber(data.items.length)} unlinked Inventory Item{data.items.length === 1 ? '' : 's'} · Items fetched {formatDateTime(data.fetched_at)}
          </span>
        )}
        <style>{'@keyframes qb-spin { to { transform: rotate(360deg); } }'}</style>
      </div>

      <QuickBooksConnectionNotice connection={data?.connection} style={{ margin: '12px 24px 0' }} />

      <div style={{ margin: '16px 24px 0' }}>
        <UnderlinedTabs tabs={lists} value={list} onChange={(key) => { setList(key); closePicker(); }} />
      </div>

      {list === 'unmatched' && (
        <BulkSelectBar
          selection={selection}
          noun="suggestions"
          actions={[{ key: 'accept', label: busy ? 'Linking...' : 'Accept suggestions', onClick: acceptSelected }]}
        />
      )}

      {list === 'items' ? (
        <div style={{ margin: '16px 24px 0', background: '#fff', border: '1px solid #b3bbc7', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <table className="list-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 16, tableLayout: 'fixed' }}>
            <colgroup>
              <col />
              <col style={{ width: '22%' }} />
              <col style={{ width: '30%' }} />
            </colgroup>
            <thead className="list-head" style={{ fontSize: 14 }}>
              <tr>
                <th style={TH}>QuickBooks Item</th>
                <th style={TH}>QuickBooks SKU</th>
                <th style={TH}>Suggested for</th>
              </tr>
            </thead>
            <tbody>
              {!data && loading ? (
                <tr><td colSpan={3} style={{ padding: 14, textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>Fetching Items from QuickBooks...</td></tr>
              ) : itemRows.length === 0 ? (
                <tr>
                  <td colSpan={3} style={{ padding: 14, textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>
                    {search ? 'No results' : connected ? 'Every active Inventory Item is linked' : 'Items can’t be fetched from QuickBooks right now'}
                  </td>
                </tr>
              ) : paging.pageRows.map(({ item, suggestedFor }) => (
                <tr key={item.id} className="list-row">
                  <td className="cell-title" style={TD}>{item.full_name}</td>
                  <td data-label="QuickBooks SKU" style={TD}>{item.sku}</td>
                  <td data-label="Suggested for" style={TD}>
                    {suggestedFor ? (
                      <button type="button" onClick={() => openProduct(suggestedFor.id)} title="Opens this Product" style={{ ...LINK_BUTTON, fontSize: 14, textDecoration: 'none', color: '#222', textAlign: 'left' }}>
                        <strong>{suggestedFor.sku}</strong> {suggestedFor.name}
                      </button>
                    ) : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
      <div style={{ margin: '16px 24px 0', background: '#fff', border: '1px solid #b3bbc7', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
        <table className="list-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 16, tableLayout: 'fixed' }}>
          {list === 'unmatched' ? (
            <>
              <colgroup>
                {editable && <col style={{ width: '4%' }} />}
                <col style={{ width: '16%' }} />
                <col style={{ width: '30%' }} />
                <col />
                {editable && <col style={{ width: '17%' }} />}
              </colgroup>
              <thead className="list-head" style={{ fontSize: 14 }}>
                <tr>
                  {editable && <th style={{ ...TH, textAlign: 'center' }}>{selection.pageIds.length > 0 && <SelectPageCheckbox selection={selection} />}</th>}
                  <th style={TH}>SKU</th>
                  <th style={TH}>Name</th>
                  <th style={TH}>Suggested QuickBooks Item</th>
                  {editable && <th style={TH}></th>}
                </tr>
              </thead>
            </>
          ) : (
            <>
              <colgroup>
                <col style={{ width: '16%' }} />
                <col style={{ width: '28%' }} />
                <col />
                <col style={{ width: '16%' }} />
                {editable && <col style={{ width: '6%' }} />}
              </colgroup>
              <thead className="list-head" style={{ fontSize: 14 }}>
                <tr>
                  <th style={TH}>SKU</th>
                  <th style={TH}>Name</th>
                  <th style={TH}>QuickBooks Item</th>
                  <th style={TH}>QuickBooks SKU</th>
                  {editable && <th style={TH}></th>}
                </tr>
              </thead>
            </>
          )}
          <tbody>
            {!data && loading ? (
              <tr><td colSpan={list === 'unmatched' ? (editable ? 5 : 3) : (editable ? 5 : 4)} style={{ padding: 14, textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>Fetching Items from QuickBooks...</td></tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={list === 'unmatched' ? (editable ? 5 : 3) : (editable ? 5 : 4)} style={{ padding: 14, textAlign: 'center', background: '#f6f8fa', fontSize: 14 }}>
                  {search ? 'No results' : { unmatched: 'Every active Product is linked', matched: 'No Products are linked yet', needs_attention: 'Every match is to an active Item' }[list]}
                </td>
              </tr>
            ) : list === 'unmatched' ? paging.pageRows.map((row) => (
              <tr key={row.product.id} className="list-row">
                {editable && (
                  <td className="cell-select" style={{ ...TD, textAlign: 'center' }}>
                    {row.suggestion && editing !== row.product.id && <SelectRowCheckbox selection={selection} id={row.product.id} />}
                  </td>
                )}
                {productCells(row)}
                <td data-label="Suggested Item" style={TD}>
                  {editing === row.product.id ? renderPicker(row.product.id) : row.suggestion ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      {itemLabel(row.suggestion.item)}
                      <Pill {...REASON_PILLS[row.suggestion.reason]}>{REASON_PILLS[row.suggestion.reason].label}</Pill>
                    </span>
                  ) : (
                    <span style={{ color: '#888', fontSize: 13 }}>{connected ? 'No suggestion' : ''}</span>
                  )}
                </td>
                {editable && (
                  <td style={{ ...TD, whiteSpace: 'nowrap' }}>
                    {editing !== row.product.id && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 12 }}>
                        {row.suggestion && (
                          <button type="button" onClick={() => link(row.product.id, row.suggestion.item)} disabled={busy} title="Links this Product to the suggested QuickBooks Item" style={{ ...SMALL_PRIMARY, cursor: busy ? 'not-allowed' : 'pointer' }}>
                            Accept
                          </button>
                        )}
                        <button type="button" onClick={() => openPicker(row.product.id)} disabled={busy} title="Picks a QuickBooks Item for this Product" style={LINK_BUTTON}>
                          {row.suggestion ? 'Choose another' : 'Choose Item'}
                        </button>
                      </span>
                    )}
                  </td>
                )}
              </tr>
            )) : paging.pageRows.map((row) => (
              <tr key={row.product.id} className="list-row">
                {productCells(row)}
                <td data-label="QuickBooks Item" style={TD}>
                  {editing === row.product.id ? renderPicker(row.product.id) : (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      {row.link.qbo_item_name}
                      <QuickBooksItemStatusPill status={row.link.qbo_item_status} />
                    </span>
                  )}
                </td>
                <td data-label="QuickBooks SKU" style={TD}>{editing === row.product.id ? '' : row.link.qbo_item_sku}</td>
                {editable && (
                  <td className="cell-actions" style={{ ...TD, textAlign: 'center' }}>
                    {editing !== row.product.id && (
                      <RowActionsMenu
                        items={[
                          { key: 'change-item', label: 'Change Item', disabled: busy, onClick: () => openPicker(row.product.id) },
                          { key: 'remove-match', label: 'Remove match', danger: true, disabled: busy, onClick: () => unlink(row) },
                        ]}
                      />
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}
      {paging.pagination.total > 0 && <LocalPagination paging={paging} totalLabel={list === 'items' ? 'Items' : 'Products'} />}
    </>
  );
}

export default QuickBooksProductsTab;
